import { moveToFulfilled } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type { CampaignCreateFulfilledAction } from '../../domains/campaignCreation/actions.types';
import type {
  CampaignEditFulfilledAction,
  CampaignPublishFulfilledAction,
} from '../../domains/campaignEdit/actions.types';
import type { CampaignLoadFulfilledAction } from '../../domains/campaigns/actions.types';
import type { FeedLoadFulfilledAction } from '../../domains/feedLoad/actions.types';
import type {
  AudienceMembersLoadFulfilledAction,
  DesignateLeadersFromOfficeGroupAsyncStatusFulfilledAction,
} from '../../domains/leadersManagement/actions.types';
import type {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import type { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import type { TeamsMeetingLoadFulfilledAction } from '../../domains/teamsMeetingLoad/actions.types';
import type { TeamsMeetingsByOrganizerFulfilledAction } from '../../domains/teamsMeetingsByOrganizer/actions.types';
import type { TypeaheadFulfilledAction } from '../../domains/typeahead/actions.types';

import type { OfficeGroup, OfficeGroups } from './types';

export type OfficeGroupAction =
  | FeedLoadFulfilledAction
  | AudienceMembersLoadFulfilledAction
  | CampaignCreateFulfilledAction
  | CampaignEditFulfilledAction
  | CampaignLoadFulfilledAction
  | CampaignPublishFulfilledAction
  | DesignateLeadersFromOfficeGroupAsyncStatusFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction
  | NestedThreadLoadFulfilledAction
  | TeamsMeetingLoadFulfilledAction
  | TeamsMeetingsByOrganizerFulfilledAction
  | TypeaheadFulfilledAction;

const initialState: OfficeGroups = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

type StateWithFulfilledOfficeGroups = (
  state: OfficeGroups,
  officeGroupsResult: Record<string, OfficeGroup> | undefined
) => OfficeGroups;
const stateWithFulfilledOfficeGroups: StateWithFulfilledOfficeGroups = (state, officeGroupsResult) => {
  if (!officeGroupsResult) {
    return state;
  }

  const fulfilledOfficeGroupIds = new Set(Object.keys(state.fulfilled.byId));

  const newOfficeGroups = Object.keys(officeGroupsResult).reduce((entities, officeGroupIdResult) => {
    const officeGroupEntityResult: OfficeGroup = officeGroupsResult[officeGroupIdResult];

    if (fulfilledOfficeGroupIds.has(officeGroupIdResult)) {
      const fulfilledOfficeGroup = state.fulfilled.byId[officeGroupIdResult];

      return {
        ...entities,
        [officeGroupIdResult]: {
          ...officeGroupEntityResult,
          name: fulfilledOfficeGroup.name || officeGroupEntityResult.name,
          memberCount: fulfilledOfficeGroup.memberCount || officeGroupEntityResult.memberCount,
        },
      };
    }

    return {
      ...entities,
      [officeGroupIdResult]: officeGroupEntityResult,
    };
  }, {});

  return moveToFulfilled(state, newOfficeGroups);
};

const reducer: Reducer<OfficeGroups, OfficeGroupAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'TYPEAHEAD.LOAD_FULFILLED':
      return stateWithFulfilledOfficeGroups(state, action.payload.entities.officeGroups);
    case 'DESIGNATE_LEADERS_FROM_OFFICE_GROUP_ASYNC_STATUS.FULFILLED':
      return stateWithFulfilledOfficeGroups(state, action.payload.entities.officeGroups);
    case 'TEAMS_MEETING.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'TEAMS_MEETINGS_BY_ORGANIZER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'CAMPAIGN.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'CAMPAIGN.CREATE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'CAMPAIGN.EDIT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'CAMPAIGN.PUBLISH_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'AUDIENCE_MEMBERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'FEED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    default:
      return state;
  }
};

export default reducer;
